import React from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import Guide from './components/Guide';
import QuickReportPage from './components/QuickReportPage';
import Pricing from './components/Pricing';
import {
  ArrowRight,
  Target,
  Handshake,
  History,
  ShieldCheck,
  UserCheck,
  Database,
  Network,
  Mail,
  Phone,
  Search,
  AlertCircle,
  Brain,
  Boxes
} from 'lucide-react';

function getInitialView(): 'home' | 'guide' | 'report' | 'pricing' {
  const path = window.location.pathname;
  if (path === '/przewodnik-po-reprezentacji-spolek-w-polsce') return 'guide';
  if (path === '/cennik') return 'pricing';
  if (path.startsWith('/sprawdz-firme') || path.startsWith('/krs')) return 'report';

  return 'home';
}

function getInitialNip(): string {
  if (typeof window === 'undefined') return '';
  const path = window.location.pathname;
  
  // Foundation slugs end with -krs-NNNNNNNNNN — don't treat KRS as NIP.
  // The foundation data is hydrated from __INITIAL_SEO_DATA__.krs instead.
  if (/-krs-\d{10}\/?$/.test(path)) return '';

  // Try to match NIP at the end of a company slug (/krs/any-string-NNNNNNNNNN)
  const krsMatch = path.match(/(\d{10})\/?$/);
  if (krsMatch) return krsMatch[1];

  // Fallback to query param
  const params = new URLSearchParams(window.location.search);
  return params.get('nip') || '';
}

const App: React.FC = () => {
  const [view, setView] = React.useState<'home' | 'guide' | 'report' | 'pricing'>(getInitialView);
  const [currentNip, setCurrentNip] = React.useState<string>(getInitialNip);

  const updateView = (newView: 'home' | 'guide' | 'report' | 'pricing', pushState = true, id?: string) => {
    setView(newView);
    window.scrollTo(0, 0);
    if (pushState) {
      let path = '/';
      if (newView === 'guide') path = '/przewodnik-po-reprezentacji-spolek-w-polsce';
      else if (newView === 'pricing') path = '/cennik';
      else if (newView === 'report') {
        const currentPath = window.location.pathname;
        const cleanId = (id || '').replace(/-/g, '').trim();
        const isKrs = cleanId.startsWith('0') && cleanId.length === 10;
        const queryParam = isKrs ? 'krs' : 'nip';

        // Don't override /krs/ URLs if we are already there and ID matches
        if (currentPath.startsWith('/krs/') && currentPath.includes(cleanId)) {
            path = currentPath; 
        } else {
            path = `/sprawdz-firme${cleanId ? `?${queryParam}=${cleanId}` : ''}`;
        }
      }
      window.history.pushState({ view: newView, nip: id }, '', path);
    }
  };

  const navigateToReport = (nip: string) => {
    setCurrentNip(nip);
    updateView('report', true, nip);
  };

  React.useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.view) {
        setView(event.state.view);
        setCurrentNip(event.state.nip || '');
        window.scrollTo(0, 0);
      } else {
        setView(getInitialView());
        setCurrentNip(getInitialNip());
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  React.useEffect(() => {
    if (view === 'guide') {
      document.title = 'Poradnik: Bezpieczna Weryfikacja Reprezentacji - Verifiko';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Poznaj zawiłości Kodeksu spółek handlowych i dowiedz się, jak bezpiecznie weryfikować reprezentację kontrahenta. Przewodnik Verifiko.');
      }
    } else if (view === 'pricing') {
      document.title = 'Cennik: Plany i Opłaty - Verifiko';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Sprawdź cennik Verifiko. Proste plany dopasowane do Twoich potrzeb. Bez ukrytych kosztów.');
      }
    } else if (view === 'report') {
      // Protect SSG title (e.g. "Kto reprezentuje...") from being overwritten by a generic one
      if (!document.title.startsWith('Kto reprezentuje')) {
        document.title = `Sprawdź Firmę${currentNip ? ` – ${currentNip}` : ''} – Verifiko`;
      }
    } else {
      document.title = 'Verifiko - Szybka i Niezawodna Weryfikacja dla Finansów i Prawa';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Niezawodne rozwiązania weryfikacyjne dla finansów i prawa. Szybka weryfikacja firm i reprezentantów.');
      }
    }
  }, [view, currentNip]);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    if (view !== 'home') {
      setView('home');
      // Small delay to let home render
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header onNavigate={(newView) => updateView(newView)} />

      <main className="flex-1">
        {view === 'home' ? (
          <>
            {/* Hero Section */}
            <section className="relative w-full pt-16 pb-12 md:pt-20 md:pb-20 lg:pt-32 lg:pb-36 overflow-hidden bg-primary">
              {/* Background Image */}
              <div className="absolute inset-0 z-0">
                <div
                  className="absolute inset-0 bg-top bg-cover opacity-30"
                  style={{ backgroundImage: 'url(/hero-bg.jpg)' }}
                >
                </div>
              </div>

              <div className="w-4/5 mx-auto px-4 md:px-6 relative z-10">
                <div className="mx-auto max-w-4xl text-center">
                  <h1 className="text-4xl font-extrabold tracking-tighter text-white sm:text-5xl md:text-6xl lg:text-7xl font-headline leading-tight">
                    Szybka i Niezawodna <br />
                    Weryfikacja dla Finansów i Prawa
                  </h1>
                  <p className="mt-8 text-lg text-slate-400 md:text-xl max-w-4xl mx-auto leading-relaxed">
                    Razem z <span className="text-accent font-extrabold uppercase tracking-wider">AI</span>, Verifiko wykręca <span className="text-accent font-medium">najszybsze</span> i <span className="text-accent font-medium">najbardziej precyzyjne</span> rozwiązanie <br className="hidden md:block" />
                    do weryfikacji firm oraz reprezentantów dla finansów i prawa.
                  </p>

                  {/* NIP Quick Check */}
                  <HeroNipForm onSubmit={navigateToReport} />

                  <div className="mt-12 flex flex-col sm:flex-row justify-center items-center gap-5">
                    <a
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-accent text-primary hover:bg-accent-hover h-12 rounded-md px-8 transform hover:-translate-y-0.5"
                      href="#contact"
                      onClick={(e) => scrollToSection(e, 'contact')}
                    >
                      Umów konsultację
                    </a>
                    <a
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-white/20 bg-transparent text-white hover:bg-white/10 h-12 rounded-md px-8"
                      href="#services"
                      onClick={(e) => scrollToSection(e, 'services')}
                    >
                      Poznaj usługi
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* About Section */}
            <section id="about" className="w-full py-24 md:py-32 bg-white">
              <div className="w-4/5 mx-auto px-4 md:px-6">
                <div className="grid gap-16 lg:grid-cols-2 lg:gap-24 items-center">
                  <div className="space-y-8">
                    <h2 className="text-3xl font-headline font-bold tracking-tighter sm:text-4xl md:text-5xl text-primary">
                      Zbudowane na <br /><span className="text-accent underline decoration-accent/30 underline-offset-8">Doświadczeniu i Innowacji</span>
                    </h2>
                    <p className="max-w-[600px] text-slate-600 md:text-lg/relaxed leading-relaxed">
                      Naszym celem jest dostarczanie narzędzi, które realnie wspierają bezpieczeństwo i efektywność finansów i prawa.
                    </p>
                    <div className="space-y-8 pt-6">
                      <div className="flex items-start gap-5">
                        <div className="bg-accent/10 p-3 rounded-xl">
                          <Target className="w-6 h-6 text-accent" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-primary">Misja</h3>
                          <p className="text-slate-600 mt-2 leading-relaxed">
                            Dostarczanie najszybszych i najbardziej niezawodnych rozwiązań weryfikacyjnych dla finansów i prawa, zwiększając bezpieczeństwo i zaufanie w biznesie. Twoja organizacja podpisuje co dzień wiele umów? Nasze usługi są dla Ciebie.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-5">
                        <div className="bg-accent/10 p-3 rounded-xl">
                          <Handshake className="w-6 h-6 text-accent" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-primary">Wartości</h3>
                          <p className="text-slate-600 mt-2 leading-relaxed">
                            Opieramy naszą działalność na doświadczeniu w biznesie i innowacyjności. Partnerstwo z klientami i dbałość o najwyższą jakość i bzepieczeństwo danych to fundamenty naszej pracy.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-5">
                        <div className="bg-accent/10 p-3 rounded-xl">
                          <History className="w-6 h-6 text-accent" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-primary">Historia</h3>
                          <p className="text-slate-600 mt-2 leading-relaxed">
                            Powstaliśmy z pasji do technologii i potrzeby usprawnienia procesów. Od początku skupiamy się na tworzeniu narzędzi idealnie dopasowanych do polskiego rynku.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center relative">
                    <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-full transform -translate-y-4"></div>
                    <img
                      alt="Abstract image of blurred code on a screen."
                      loading="lazy"
                      width="600"
                      height="600"
                      className="relative rounded-2xl object-cover shadow-2xl aspect-square border-2 border-slate-100"
                      src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NDE5ODJ8MHwxfHNlYXJjaHwxfHxjb2RlfGVufDB8fHx8MTc2NTQ2ODg2MXww&ixlib=rb-4.1.0&q=80&w=1080"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Services Section */}
            <section id="services" className="w-full py-24 md:py-32 bg-slate-50">
              <div className="w-4/5 mx-auto px-4 md:px-6">
                <div className="flex flex-col items-center justify-center space-y-4 text-center mb-16">
                  <div className="space-y-4">
                    <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl font-headline text-primary">
                      Kompleksowe rozwiązanie weryfikacyjne
                    </h2>
                    <p className="max-w-[900px] text-slate-600 md:text-xl/relaxed mx-auto">
                      Oferujemy zakres usług, które pomogą zminimalizować ryzyko i automatyzować procesy w Twojej organizacji.
                    </p>
                  </div>
                </div>
                <div className="mx-auto grid max-w-6xl items-start gap-8 py-4 sm:grid-cols-2 md:grid-cols-3">
                  {[
                    { icon: Brain, title: "Inteligentna weryfikacja AI", desc: "Wykorzystaj potencjał zaawansowanych modeli językowych do błyskawicznej analizy i interpretacji skomplikowanych zapisów o reprezentacji spółek." },
                    { icon: ShieldCheck, title: "Weryfikacja rejestrowa", desc: "Kompleksowe sprawdzanie danych podmiotów oraz ich reprezentantów bezpośrednio w bazach KRS i CEIDG w czasie rzeczywistym." },
                    { icon: UserCheck, title: "Analiza uprawnień", desc: "Zaawansowana ocena zdolności reprezentantów do zaciągania zobowiązań, uwzględniająca progi kwotowe i rodzaje czynności." },
                    { icon: Database, title: "Dostęp API i Integracje", desc: "Pełny dostęp do ujednoliconych danych weryfikacyjnych poprzez elastyczne API, gotowe do integracji z Twoimi systemami." },
                    { icon: Boxes, title: "Dedykowane Rozwiązania", desc: "Konteneryzowane, bezpieczne usługi dostarczane on-demand dla dużych organizacji wymagających najwyższej wydajności i izolacji danych." },
                    { icon: Network, title: "Bezpieczeństwo i Infrastruktura", desc: "Bezpieczne przetwarzanie danych w infrastrukturze chmurowej w regionach EU i zgodność z RODO / GDPR." }
                  ].map((service, index) => (
                    <div key={index} className="group relative rounded-xl border border-slate-200 bg-white p-8 shadow-sm hover:shadow-xl hover:border-accent/50 transition-all duration-300 h-full">

                      <div className="flex flex-col space-y-4">
                        <div className="bg-slate-50 p-4 rounded-xl self-start group-hover:bg-accent/10 transition-colors">
                          <service.icon className="h-8 w-8 text-primary group-hover:text-accent transition-colors" />
                        </div>
                        <div className="space-y-2">
                          <div className="font-bold tracking-tight text-xl text-primary">{service.title}</div>
                          <div className="text-slate-500 leading-relaxed">{service.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Pricing Section (Preview) */}
            <section id="pricing" className="w-full py-24 md:py-32 bg-white">
              <div className="w-4/5 mx-auto px-4 md:px-6">
                <div className="flex flex-col items-center justify-center space-y-4 text-center mb-16">
                  <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl font-headline text-primary">
                    Prosty i przejrzysty cennik
                  </h2>
                  <p className="max-w-[700px] text-slate-600 md:text-xl/relaxed mx-auto">
                    Wybierz plan dopasowany do Twoich potrzeb. Bez automatycznych płatności.
                  </p>
                </div>

                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 max-w-[90rem] mx-auto">
                  {[
                    { name: 'Free', price: '0 PLN', desc: '10 raportów / mies (domyślny)' },
                    { name: 'Start', price: '25 PLN', desc: '5 raportów / mies' },
                    { name: 'Pro', price: '125 PLN', desc: '25 raportów / mies', highlighted: true },
                    { name: 'Business', price: '500 PLN', desc: '100 raportów / mies' },
                    { name: 'Enterprise', price: 'Zapytaj', desc: 'Dedykowane rozwiązania' }
                  ].map((plan, i) => (
                    <div key={i} className={`p-8 rounded-2xl border text-center transition-all ${plan.highlighted ? 'border-accent shadow-xl bg-slate-50 relative' : 'border-slate-100 bg-white'}`}>
                      {plan.highlighted && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-primary text-[10px] font-bold px-3 py-1 rounded-full uppercase">Najczęściej wybierany</div>}
                      <h3 className="text-xl font-bold text-primary mb-2">{plan.name}</h3>
                      <div className="text-3xl font-extrabold text-primary mb-4">{plan.price}</div>
                      <p className="text-slate-500 text-sm mb-6">{plan.desc}</p>
                      <button
                        onClick={() => updateView('pricing')}
                        className={`w-full h-10 rounded-lg font-bold text-sm transition-all ${plan.highlighted ? 'bg-accent text-primary hover:bg-accent-hover' : 'bg-slate-100 text-primary hover:bg-slate-200'}`}
                      >
                        Szczegóły planu
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-12 text-center">
                  <a
                    href="/cennik"
                    onClick={(e) => { e.preventDefault(); updateView('pricing'); }}
                    className="text-primary font-bold hover:text-accent transition-colors flex items-center justify-center gap-2"
                  >
                    Zobacz pełny cennik i zasady <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section id="cta" className="w-full bg-primary border-y border-white/5 relative overflow-hidden">
              {/* Decorative glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl pointer-events-none"></div>

              <div className="w-4/5 mx-auto px-4 md:px-6 py-20 md:py-28 relative z-10">
                <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-20">
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl font-headline text-white leading-tight">
                      Gotowy, by zwiększyć <br />
                      <span className="text-accent">Bezpieczeństwo i Efektywność?</span>
                    </h2>
                    <p className="text-lg text-slate-400 max-w-md">
                      Porozmawiajmy o tym, jak Verifiko może wesprzeć Twoją firmę. Umów się na bezpłatną konsultację.
                    </p>
                  </div>
                  <div className="flex justify-start lg:justify-end">
                    <a
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-base font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-14 rounded-md px-10 bg-accent text-primary hover:bg-accent-hover shadow-[0_0_20px_rgba(0,225,173,0.3)] hover:shadow-[0_0_30px_rgba(0,225,173,0.5)] cursor-pointer transform hover:-translate-y-1"
                      href="#contact"
                      onClick={(e) => scrollToSection(e, 'contact')}
                    >
                      Umów konsultację
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="w-full py-24 md:py-32 bg-white">
              <div className="w-4/5 mx-auto grid items-center justify-center gap-12 px-4 text-center md:px-6">
                <div className="space-y-4">
                  <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight font-headline text-primary">
                    Skontaktuj się z Nami
                  </h2>
                  <p className="mx-auto max-w-[600px] text-slate-600 md:text-xl/relaxed">
                    Masz pytania? Chcesz dowiedzieć się więcej? Skontaktuj się z nami mailowo lub telefonicznie.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-10 text-primary font-medium">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-accent/10 rounded-full text-accent">
                      <Mail className="h-5 w-5" />
                    </div>
                    <a href="mailto:kontakt@verifiko.pl" className="hover:text-accent transition-colors">kontakt@verifiko.pl</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-accent/10 rounded-full text-accent">
                      <Phone className="h-5 w-5" />
                    </div>
                    <a href="tel:+48518929623" className="hover:text-accent transition-colors">+48 518 929 623</a>
                  </div>
                </div>
              </div>
            </section>
          </>
        ) : view === 'guide' ? (
          <Guide onBack={() => updateView('home')} />
        ) : view === 'pricing' ? (
          <Pricing onBack={() => updateView('home')} />
        ) : (
          <QuickReportPage
            nip={currentNip}
            onCheckNip={(newNip: string) => { setCurrentNip(newNip); }}
          />
        )}
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Hero NIP form (inline, keeps App.tsx self-contained)
───────────────────────────────────────────────────────── */
function HeroNipForm({ onSubmit }: { onSubmit: (nip: string) => void }) {
  const [value, setValue] = React.useState('');
  const [error, setError] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = value.replace(/-/g, '').replace(/\s/g, '').trim();
    if (!/^\d{10}$/.test(cleaned)) {
      setError('Podaj 10-cyfrowy numer NIP lub KRS.');
      return;
    }
    setError('');
    onSubmit(cleaned);
  };

  return (
    <div id="hero-nip-form" className="mt-12 mb-4 scroll-mt-24">
      <p className="text-sm text-accent mb-4 font-bold tracking-wide uppercase">
        Sprawdź spółkę lub organizację zanim podpiszesz umowę
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
          <input
            id="hero-nip-input"
            type="text"
            inputMode="numeric"
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(''); }}
            placeholder="Numer NIP lub KRS..."
            maxLength={13}
            className="w-full pl-10 pr-4 h-12 rounded-lg border-2 border-white/20 bg-white/95 text-slate-900 text-sm font-medium placeholder:text-slate-500 focus:outline-none focus:border-accent focus:bg-white hover:bg-white transition-colors"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-lg bg-accent text-primary font-bold text-sm hover:bg-accent-hover transition-all shadow-[0_0_20px_rgba(0,225,173,0.4)] whitespace-nowrap shrink-0"
        >
          Sprawdź <ArrowRight className="w-4 h-4" />
        </button>
      </form>
      {error && (
        <p className="mt-3 text-sm text-red-400 flex items-center justify-center gap-1.5 font-medium">
          <AlertCircle className="w-4 h-4" />{error}
        </p>
      )}
    </div>
  );
}

export default App;